// trace-viewer/src/editor/TriplanarPreview.ts
//
// EDITOR-ONLY CODE (the MaterialPreviews precedent). The runtime viewer does
// not support triplanar at all — no behavior installs a projection, and a
// published scene is vanilla glTF whose UVs the export bake produces (stack
// PR #677). This module is the live WYSIWYG preview an editor host opts into
// so a creator stretching a component sees what that bake will produce.
import type { Material } from "@babylonjs/core/Materials/material";
import type { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { Node as BabylonNode } from "@babylonjs/core/node";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { TraceScene } from "../core/TraceScene";
import type { SceneObject } from "../core/SceneObject";
import { MorphStretch } from "../behaviors/morphstretch";
import { MeshBackground } from "../behaviors/meshbackground";
import { applyTriplanar, disableTriplanar, getTriplanarPlugin } from "./TriplanarMaterial";
import { EDITOR_EVENTS } from "./undo/editor-events";
import { resolveTriplanarPolicy } from "../export/triplanarPolicy";
import { cloneMaterial } from "../utils/materialClone";

/**
 * Live triplanar preview — the editor-scoped OWNER of every projection shown
 * while a scene is being authored.
 *
 * Why an owner at all (Martin's #646 bug 2): the old hierarchy pass cloned
 * shared factory materials per placement and then FORGOT them — nothing
 * restored or disposed the clones, so every place/delete/reload cycle
 * accumulated materials whose plugins pinned disposed anchor nodes. This
 * module keeps a registry of every clone it installs, keyed by the object
 * root it projected for; a refresh reuses recognized clones, `clear` restores
 * the original materials and disposes the clones, and registry entries whose
 * root was disposed (delete, src-swap reload — reload mints a NEW transform
 * root) are swept on every refresh so no dead TransformNode is ever pinned.
 *
 * What gets projected: for every SceneObject carrying MorphStretch or
 * MeshBackground — the two behaviors that reshape geometry under authored
 * materials — every qualifying material on its own meshes (any PBR material with an
 * albedo/bump/ORM texture; see resolveTriplanarPolicy — an asset-authored
 * `extras.trace.triplanar` tag beats the name heuristic, `false` opts out).
 * Materials are cloned per (material, object root) before projecting, because
 * ModelLoader instantiates with cloneMaterials: false: placements of one GLB
 * share material OBJECTS, and a plugin's anchor is per-material state —
 * projecting the shared object would re-anchor every other placement.
 *
 * Play mode projects, deliberately. See the note on the play-mode listener in
 * {@link enableTriplanarPreview}: the runtime does not project because export
 * bakes the projection into real UVs, not because the projected picture is
 * wrong — so an editor previewing the deliverable should show it.
 *
 * Refresh timing: a host calls {@link enableTriplanarPreview} ONCE and the
 * engine drives the rest — see "Self-driving refresh" below for which signals
 * cover which load paths. The explicit {@link refreshTriplanarPreview} entry
 * points remain for a host that wants to drive timing itself (and are what the
 * self-drive calls internally). Behavior commits are covered WITHOUT a host
 * call: EVERY mesh under a qualifying object carries a material-change
 * observer — not only the slots projecting right now, because the mesh a
 * Renderer will swap its tiled material onto is wearing a factory material
 * until that swap lands. So an owner swapping a material onto any of its
 * meshes — a Renderer property-override commit, a play-mode state-driven
 * swap, the Renderer's own material GLB finishing its load — schedules a
 * microtask re-refresh of that root, whose walk re-adopts the new material
 * in place (the disabled-plugin branch below). The observers are inert
 * between swaps and bounded by the object's mesh count: `dropObservers`
 * releases them whenever the root is cleared, re-projected from scratch, or
 * disposed. The walk is idempotent — a material already projected for its
 * root is re-applied in place, never re-cloned — and its own swaps never
 * re-trigger the observers.
 *
 * Published output reads nothing from this preview either way: the
 * projection is baked into real UVs at export (TraceExport's bake, landing
 * in stack PR #677).
 */

interface SwapRecord {
    /** What the mesh wore before this module swapped it. */
    original: Material;
    /** The clone this module installed in its place. */
    clone: PBRMaterial;
}

interface RootPreview {
    /** The object whose root this entry projects for (its transform at refresh time). */
    so: SceneObject;
    /** original material → the clone this module installed for `root`. */
    clones: Map<Material, PBRMaterial>;
    /** mesh → its current swap record (original restored on clear). */
    swapped: Map<AbstractMesh, SwapRecord>;
    /**
     * Foreign per-object clones this module re-projected IN PLACE — e.g. a
     * Renderer tint clone: Babylon's `clone()` recreates the plugin via its
     * registered class but with default (disabled) state, so the projection
     * silently drops. Those materials are object-private by construction
     * (whoever cloned did so for this object's slot), so no second clone is
     * needed; `clear` merely disables their plugin again.
     */
    adopted: Set<PBRMaterial>;
    /**
     * Material-change observers on every mesh this entry projected — the
     * behavior-commit → re-project edge. When an owner swaps a projected
     * slot's material under us (a Renderer property-override clone, a
     * play-mode state swap), the observer schedules a microtask re-refresh
     * of this root; the walk then re-adopts the new clone in place. Removed
     * on clear and on the disposed-root sweep.
     */
    observers: Map<AbstractMesh, Observer<AbstractMesh>>;
}

/** Every preview clone this module installed, keyed by object root. */
const REGISTRY = new Map<TransformNode, RootPreview>();

/**
 * Clones of ours stranded at clear time because an interposing owner's
 * material was worn instead (see clearEntry): each is kept alive — the owner
 * may still hand it back on its own restore path — and tracked against the
 * root it projected for. sweepDisposedRoots disposes one once its root is
 * gone (nothing left to hand back to) and disposeTriplanarPreview disposes
 * the rest at scene teardown, so none is ever unreachable-but-alive.
 */
const KEEP_ALIVE = new Map<PBRMaterial, TransformNode>();

/** Roots with a scheduled microtask re-refresh — one in flight per root. */
const PENDING_REFRESH = new Set<TransformNode>();

/** True while refreshRoot itself assigns materials — its own swaps must not schedule refreshes. */
let REFRESHING = false;

/**
 * Typed class lookups (the same form MorphStretch's own MeshBackground poke
 * took after the #679 review; no import cycle exists in either direction):
 * the preview cares that geometry reshapes under the materials, not how —
 * and the typed overload makes a behavior rename a build error here instead
 * of a silently always-false qualifies().
 */
function qualifies(so: SceneObject): boolean {
    return !!(so.getBehavior(MorphStretch) || so.getBehavior(MeshBackground));
}

/**
 * Drop registry entries whose root no longer exists. Restoration is
 * impossible (the meshes are gone with the root); what matters is that the
 * clones — whose plugins pin the disposed anchor — are disposed rather than
 * accumulated. Keep-alive clones are swept on the same trigger: once the
 * root they projected for is gone, no owner can hand them back to its
 * meshes, so disposal is finally safe. Runs at the head of every
 * refresh/clear entry point.
 */
function sweepDisposedRoots(): void {
    for (const [root, entry] of REGISTRY) {
        if (!root.isDisposed()) continue;
        dropObservers(entry);
        for (const clone of entry.clones.values()) {
            if (!wasDisposed(clone)) clone.dispose();
        }
        REGISTRY.delete(root);
    }
    for (const [clone, root] of KEEP_ALIVE) {
        if (wasDisposed(clone)) {
            KEEP_ALIVE.delete(clone);
            continue;
        }
        if (root.isDisposed()) {
            clone.dispose();
            KEEP_ALIVE.delete(clone);
        }
    }
}

/** Babylon materials carry no public disposed flag; scene removal is the tell. */
function wasDisposed(material: Material): boolean {
    const scene = material.getScene();
    return !scene || scene.isDisposed || scene.materials.indexOf(material) === -1;
}

function entryFor(so: SceneObject): RootPreview {
    let entry = REGISTRY.get(so.transform);
    if (!entry) {
        entry = { so, clones: new Map(), swapped: new Map(), adopted: new Set(), observers: new Map() };
        REGISTRY.set(so.transform, entry);
    }
    return entry;
}

/**
 * Watch a projected slot for material swaps by other owners. Babylon's
 * material setter notifies only on an actual change, and refreshRoot's own
 * assignments are masked by REFRESHING — so this fires exactly when a
 * Renderer commit (or state swap) clones the slot's material under us, the
 * edge that used to drop the projection until the next host refresh.
 */
function observeSlot(entry: RootPreview, mesh: AbstractMesh): void {
    if (entry.observers.has(mesh)) return;
    const so = entry.so;
    const observer = mesh.onMaterialChangedObservable.add(() => {
        if (REFRESHING) return; // our own swap/restore
        scheduleRefresh(so);
    });
    if (observer) entry.observers.set(mesh, observer);
}

/** Re-refresh `so` on the next microtask — deduped per root, skipped if the entry died meanwhile. */
function scheduleRefresh(so: SceneObject): void {
    const root = so.transform;
    if (PENDING_REFRESH.has(root)) return;
    PENDING_REFRESH.add(root);
    queueMicrotask(() => {
        PENDING_REFRESH.delete(root);
        if (root.isDisposed() || !REGISTRY.has(root)) return; // cleared/deleted since
        refreshTriplanarPreviewForObject(so);
    });
}

function dropObservers(entry: RootPreview): void {
    for (const [mesh, observer] of entry.observers) {
        mesh.onMaterialChangedObservable.remove(observer);
    }
    entry.observers.clear();
}

/**
 * Project (or converge) every qualifying material on `so`'s own meshes,
 * anchored to its root. A child object's meshes are its own: MorphStretch,
 * MeshBackground and the export bake stop at the same boundary, and a reload
 * of this root must not take a child's material with it.
 */
function refreshRoot(so: SceneObject): void {
    const wasRefreshing = REFRESHING;
    REFRESHING = true;
    try {
        refreshRootInner(so);
    } finally {
        REFRESHING = wasRefreshing;
    }
}

function refreshRootInner(so: SceneObject): void {
    const root = so.transform;
    // Read per bind: an AnimationSequence creates the root when it loads,
    // which can be after this walk projected the object's materials.
    const animationRoot = () => so.animationRoot;
    const entry = entryFor(so);
    for (const mesh of so.getOwnedMeshes()) {
        // An InstancedMesh cannot own a material (it renders with its
        // source's) — cloning is impossible, skip rather than stomp.
        if ((mesh as { isAnInstance?: boolean }).isAnInstance) continue;
        // Watch EVERY mesh of a qualifying object for a material swap, not only
        // the ones projecting right now. A Renderer finishes loading its
        // material GLB after the meshes exist and swaps it in; a mesh still
        // wearing its factory material at that point was never observed, so
        // the tiled material arrived and nothing re-projected until an
        // unrelated edit or a play-mode toggle did.
        observeSlot(entry, mesh);
        const material = mesh.material;
        if (!material || material.getClassName() !== "PBRMaterial") continue;

        const record = entry.swapped.get(mesh);
        const plugin = getTriplanarPlugin(material);
        // Any plugin — enabled or not — proves the material descends from a
        // projected clone chain rooted at the mesh's recorded pre-preview
        // original. Clones along that chain often carry caller-assigned
        // NAMES ("Sofa_mat0", a Renderer tint clone), so family tuning
        // additionally reads the recorded original's library name. A
        // plugin-less material is FRESH (a factory material or an outright
        // src swap) and must resolve on its own name alone — a stale record
        // there would tune the new material by the old one's family.
        const config = plugin && record
            ? resolveTriplanarPolicy(material, material.name, record.original.name)
            : resolveTriplanarPolicy(material, material.name);
        if (!config) {
            // Converge: a material that stopped qualifying (e.g. a
            // `triplanar: false` tag landed with a src swap) gives the mesh
            // its original back if it is still wearing our clone.
            if (record && material === record.clone) {
                mesh.material = record.original;
                entry.swapped.delete(mesh);
            } else if (entry.adopted.has(material as PBRMaterial) || plugin?.anchorNode === root) {
                // An adopted foreign clone (or a handed-back clone we
                // anchored here before) converges the same way: take the
                // projection off in place — there is no original to restore,
                // its owner put it on the mesh. Left enabled, a plugin whose
                // texture refs were since disposed pins isReadyForSubMesh
                // false and the mesh stops rendering.
                disableTriplanar(material);
                entry.adopted.delete(material as PBRMaterial);
            }
            continue;
        }

        if (plugin?.anchorNode === root) {
            // Our own clone (or a previously adopted material) — re-apply in
            // place; applyTriplanar is idempotent and re-reads the texture
            // slots, so a map that appeared since last refresh projects too.
            applyTriplanar(material as PBRMaterial, config, root, animationRoot);
            continue;
        }
        if (plugin && !plugin.isEnabled) {
            // A foreign clone of a projected material (style tint, state
            // swap): Babylon's clone() recreated the plugin in default
            // (disabled) state — the plugin proves its ancestry, the
            // disabled default proves nobody owns it. Re-project in place —
            // the replacement for the removed Renderer clone-carry. No
            // second clone: the material is object-private by construction.
            applyTriplanar(material as PBRMaterial, config, root, animationRoot);
            entry.adopted.add(material as PBRMaterial);
            continue;
        }
        // Remaining cases clone per (material, root): a shared factory
        // material (no plugin), or a material carrying a plugin enabled for
        // ANOTHER root — some other placement's projection; never re-anchor
        // a material that isn't ours.
        let clone = entry.clones.get(material);
        if (!clone || wasDisposed(clone)) {
            clone = cloneMaterial(material as PBRMaterial, material.name);
            entry.clones.set(material, clone);
        }
        // (Re-)record the swap. A stale record (the mesh wore a DIFFERENT
        // material than last time — e.g. a Renderer src swap replaced the
        // slot outright) is overwritten: clear must hand back what the
        // current owner put on the mesh, not a pre-swap ancestor.
        entry.swapped.set(mesh, { original: material, clone });
        mesh.material = clone;
        applyTriplanar(clone, config, root, animationRoot);
    }
}

/** Restore + dispose one registry entry. The root may be live or half-gone. */
function clearEntry(root: TransformNode, entry: RootPreview): void {
    // Observers go first so the restores below cannot schedule refreshes.
    dropObservers(entry);
    // A clone stays alive (skipping dispose below) when a live mesh wears
    // neither the clone nor its original: an interposed owner — a Renderer
    // tint clone, say — layered its own material on top and may hand OUR
    // clone back on its own restore path. Disposing it would break that
    // hand-back, so it moves to the module-level KEEP_ALIVE tracker: the
    // sweep disposes it once this root is gone, and disposeTriplanarPreview
    // catches the rest at scene teardown (see KEEP_ALIVE).
    const keepAlive = new Set<Material>();
    for (const [mesh, record] of entry.swapped) {
        if (mesh.isDisposed()) continue;
        if (mesh.material === record.clone) {
            mesh.material = record.original;
        } else if (mesh.material !== record.original && !wasDisposed(record.original)) {
            keepAlive.add(record.clone);
            KEEP_ALIVE.set(record.clone, root);
        }
        // A DISPOSED original means the hand-back this keep-alive exists for
        // can never happen: we cloned an owner's clone (Renderer's slot
        // clone, say), the owner then restored its slot and disposed what we
        // cloned from. Nothing will ever put our clone back on a mesh, so it
        // falls through to the dispose loop instead of being pinned in
        // KEEP_ALIVE until the root itself goes away.
    }
    for (const clone of entry.clones.values()) {
        if (!keepAlive.has(clone) && !wasDisposed(clone)) clone.dispose();
    }
    for (const adopted of entry.adopted) {
        if (wasDisposed(adopted)) continue;
        // Not ours to dispose (Renderer owns its clone) — just take the
        // projection back off and release the anchor + texture refs.
        disableTriplanar(adopted);
    }
    REGISTRY.delete(root);
}

/**
 * Refresh the live triplanar preview for the whole scene: project every
 * SceneObject carrying MorphStretch or MeshBackground, converge objects that
 * stopped qualifying back to their original materials, and sweep registry
 * entries for disposed roots. Idempotent — safe to call after any edit; for
 * a single-object edit prefer {@link refreshTriplanarPreviewForObject}.
 */
export function refreshTriplanarPreview(traceScene: TraceScene): void {
    sweepDisposedRoots();
    for (const so of traceScene.getSceneObjects()) {
        if (so.transform.isDisposed()) continue;
        if (qualifies(so)) {
            refreshRoot(so);
        } else {
            const entry = REGISTRY.get(so.transform);
            if (entry) clearEntry(so.transform, entry);
        }
    }
}

/**
 * Subtree variant: refresh `so` and every descendant SceneObject — the call
 * for a tool that just placed/staged/restyled one object tree or reloaded
 * one src.
 */
export function refreshTriplanarPreviewForObject(so: SceneObject): void {
    sweepDisposedRoots();
    if (so.transform.isDisposed()) return;
    for (const candidate of [so, ...so.getDescendants()]) {
        if (candidate.transform.isDisposed()) continue;
        if (qualifies(candidate)) {
            refreshRoot(candidate);
        } else {
            const entry = REGISTRY.get(candidate.transform);
            if (entry) clearEntry(candidate.transform, entry);
        }
    }
}

/**
 * Restore `so`'s subtree to its original materials and dispose every clone
 * this module installed for it. The un-preview: the scene renders exactly as
 * the runtime viewer would.
 */
export function clearTriplanarPreviewForObject(so: SceneObject): void {
    sweepDisposedRoots();
    for (const candidate of [so, ...so.getDescendants()]) {
        const entry = REGISTRY.get(candidate.transform);
        if (entry) clearEntry(candidate.transform, entry);
    }
}

/**
 * Full teardown for one scene: restore/dispose every registry entry whose
 * root lives in `traceScene` (disposed roots included). Call when the editor
 * session ends or the host swaps scenes.
 */
export function disposeTriplanarPreview(traceScene: TraceScene): void {
    AUTO.get(traceScene)?.dispose();
    const scene = traceScene.getBabylonScene();
    for (const [root, entry] of REGISTRY) {
        if (root.isDisposed()) {
            dropObservers(entry);
            for (const clone of entry.clones.values()) {
                if (!wasDisposed(clone)) clone.dispose();
            }
            REGISTRY.delete(root);
            continue;
        }
        if (root.getScene() === scene) clearEntry(root, entry);
    }
    // Keep-alive clones are this scene's teardown debt too — including any
    // the clearEntry calls above just stranded. Nothing survives the
    // session: the hand-back they waited for can no longer happen.
    for (const [clone, root] of KEEP_ALIVE) {
        if (wasDisposed(clone)) {
            KEEP_ALIVE.delete(clone);
            continue;
        }
        if (root.isDisposed() || clone.getScene() === scene) {
            clone.dispose();
            KEEP_ALIVE.delete(clone);
        }
    }
}

// ── Self-driving refresh ────────────────────────────────────────────────────

/**
 * How long to coalesce refresh requests. A style apply or a design-set swap
 * fires a burst of resource/behavior events; one scene walk per burst is the
 * point of the window.
 */
export const TRIPLANAR_REFRESH_DEBOUNCE_MS = 150;

interface AutoRefreshState {
    timer: ReturnType<typeof setTimeout> | null;
    dispose: () => void;
}

const AUTO = new Map<TraceScene, AutoRefreshState>();

/**
 * Keep the preview correct for `traceScene` without the host calling refresh.
 *
 * The preview used to be pull-based: hosts refreshed after scene load and
 * after every edit that swapped materials or objects. That put the same five
 * call sites in every editor, and an editor that missed one got a silently
 * wrong picture rather than an error — a smart component whose texture smears
 * because nothing re-projected it. It also could not cover ADDITIVE loads at
 * all, because those finish inside the engine with no host chokepoint to hang
 * the call on.
 *
 * So the engine drives it. One call enables it for the session; from then on
 * a refresh is scheduled whenever the scene says something changed:
 *
 * - `sceneobject:reloaded` — an asset under an object was replaced, so
 *   whatever was attached to its meshes is gone. This is the per-object
 *   reload path; it does NOT fire for an additive import.
 * - topology changed — the signal that does cover an additive import. Both
 *   additive builders emit it after `_attachBehaviors()`, so the walk runs at
 *   the first moment there is anything to project. The host's
 *   `resources-changed` fires before that setup completes, which is why it
 *   cannot be the only cover: a refresh scheduled off it alone can find no
 *   materials and never re-run.
 * - `editor:resources-changed` — a place, a src swap, a design-set swap.
 * - `editor:behavior-changed` — a commit that may have added or removed the
 *   MorphStretch/MeshBackground that decides whether an object qualifies.
 *   These two are WINDOW events, so with two enabled scenes in one window a
 *   change in either schedules a coalesced refresh in both — a cheap walk
 *   that finds nothing to do, not a correctness problem. The other signals
 *   are per scene.
 * - a mesh added to the scene — a load reports done before Babylon has
 *   instantiated a GLB's meshes, and a big scene streams them in. Only
 *   meshes whose owning object qualifies (or that are not yet under any
 *   object) schedule, so a plain model streaming in costs no walks.
 *
 * Still opt-in, and deliberately so: the runtime viewer must never project.
 * Enabling is the editor saying "this is an authoring session" — it is not a
 * per-edit responsibility. Play mode keeps its projections (see below).
 *
 * Idempotent per scene. Returns a disposer; {@link disposeTriplanarPreview}
 * also detaches, so a host that already tears down needs no extra call.
 */
export function enableTriplanarPreview(traceScene: TraceScene): () => void {
    const existing = AUTO.get(traceScene);
    if (existing) return existing.dispose;

    const state: AutoRefreshState = { timer: null, dispose: () => { } };

    const schedule = (): void => {
        if (state.timer !== null) clearTimeout(state.timer);
        state.timer = setTimeout(() => {
            state.timer = null;
            refreshTriplanarPreview(traceScene);
        }, TRIPLANAR_REFRESH_DEBOUNCE_MS);
    };

    const events = traceScene.getEventSystem();
    events?.addEventListener('sceneobject:reloaded', schedule);
    // The signal that actually covers an additive import. The host's
    // resources-changed fires BEFORE async behavior setup finishes (the
    // sandbox says so at its dispatch site), so a refresh scheduled off it can
    // land on a scene whose Renderers have not built their materials yet — and
    // nothing re-runs, so the first import stays unprojected until some later
    // edit pokes it. Both additive builders emit topology AFTER
    // `_attachBehaviors()`, which is the earliest point the walk can find
    // anything to project.
    const offTopology = traceScene.onTopologyChanged(schedule);
    // Meshes arrive AFTER a load says it is done. A Model fetches its GLB
    // during load(), but Babylon instantiates the meshes asynchronously and a
    // big scene streams them in for a while — so a refresh scheduled off any
    // of the signals above can land on objects that have nothing to project
    // yet, and nothing re-runs. The first projection then waited for some
    // unrelated edit or a play-mode toggle. The scene's own mesh-added
    // observable is the honest signal; the debounce folds a whole stream of
    // arrivals into one pass. (The preview itself never adds meshes — it
    // clones materials — so this cannot feed back into itself.)
    const babylonScene = traceScene.getBabylonScene();
    const onMeshAdded = (mesh: AbstractMesh): void => {
        // A mesh is never an object's root, so the owner is found by walking
        // UP to the nearest object. Only that owner projects the mesh. One
        // under no object at all (parented after this tick) might be ours.
        for (let node: BabylonNode | null = mesh.parent; node; node = node.parent) {
            const so = traceScene.findSceneObjectByTransform(node);
            if (!so) continue;
            if (qualifies(so)) schedule();
            return;
        }
        schedule();
    };
    const meshAdded = babylonScene.onNewMeshAddedObservable.add(onMeshAdded);
    // Play mode KEEPS its projections, and re-projects on the way in.
    //
    // I removed them here first, reasoning that play mode should look like the
    // runtime and the runtime never projects. The second half is true and the
    // conclusion does not follow. The runtime does not project because it does
    // not have to: export bakes the projection into real TEXCOORD_0 UVs, so a
    // published file already shows the tiled result in a viewer that has never
    // heard of Trace. Clearing the preview therefore showed a third picture —
    // one that matches neither the editor nor the published file, and that no
    // consumer will ever render. Pressing play made textures visibly snap to
    // stretched.
    //
    // The principle this was meant to serve (DESIGN_PRINCIPLES §5: custom
    // rendering tech is editor-scoped preview, never a runtime dependency of
    // published content) is satisfied by the BAKE, not by the gate: nothing
    // ships depending on the plugin. Play mode is an editor affordance for
    // previewing the deliverable, and the deliverable is projected.
    //
    // Entering play mode still schedules a refresh, because a play-mode state
    // or variant can swap a material and the projection has to follow it.
    const onPlayMode = (): void => schedule();
    events?.addEventListener('trace-viewer:playmode-changed', onPlayMode);
    const onEditorEvent = (): void => schedule();
    const win = typeof window === 'undefined' ? null : window;
    win?.addEventListener(EDITOR_EVENTS.RESOURCES_CHANGED, onEditorEvent);
    win?.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onEditorEvent);
    // Detach when the scene goes, not only when a host remembers to say so.
    // The subscription outlives its scene otherwise: the entry keeps the
    // TraceScene reachable, and `isTriplanarPreviewEnabled` keeps answering
    // true for something that no longer exists. A host that opens scenes in
    // a loop leaks one entry per scene. Idempotent with the explicit path —
    // everything below is null-guarded or a delete.
    const sceneDisposal = babylonScene.onDisposeObservable.add(() => state.dispose());

    state.dispose = () => {
        if (state.timer !== null) clearTimeout(state.timer);
        state.timer = null;
        events?.removeEventListener('sceneobject:reloaded', schedule);
        events?.removeEventListener('trace-viewer:playmode-changed', onPlayMode);
        offTopology();
        babylonScene.onNewMeshAddedObservable.remove(meshAdded);
        win?.removeEventListener(EDITOR_EVENTS.RESOURCES_CHANGED, onEditorEvent);
        win?.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onEditorEvent);
        babylonScene.onDisposeObservable.remove(sceneDisposal);
        AUTO.delete(traceScene);
    };
    AUTO.set(traceScene, state);

    refreshTriplanarPreview(traceScene);
    return state.dispose;
}

/** Whether the engine is driving refreshes for this scene. */
export function isTriplanarPreviewEnabled(traceScene: TraceScene): boolean {
    return AUTO.has(traceScene);
}

/**
 * Run a scheduled refresh now instead of waiting out the window — for tests,
 * and for a host that must render a correct frame immediately (a thumbnail
 * capture, an export preflight).
 *
 * A no-op when nothing is scheduled: it fast-forwards a pending refresh, it
 * does not force one. A host that changed something without any signal
 * firing wants {@link refreshTriplanarPreview} instead.
 */
export function flushTriplanarPreview(traceScene: TraceScene): void {
    const state = AUTO.get(traceScene);
    if (!state || state.timer === null) return;
    clearTimeout(state.timer);
    state.timer = null;
    refreshTriplanarPreview(traceScene);
}

/** Test hook: number of live registry entries (leak assertions). */
export function _triplanarPreviewRegistrySize(): number {
    return REGISTRY.size;
}

/** Test hook: number of tracked keep-alive clones (leak assertions). */
export function _triplanarPreviewKeepAliveSize(): number {
    return KEEP_ALIVE.size;
}
